import { ITeamResponseSchema } from '@monetize/types/app';
import {
  DEFAULT_PAGER,
  FilterType,
  TDataTablePager,
} from '@monetize/types/common';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link as ReactDomLink, useNavigate } from 'react-router-dom';
import { useGetTeams } from '../../../../../api/settingsService';
import {
  MButton,
  MFlex,
  MHStack,
  MPageContainer,
  MPageSearchInput,
  MSettingsPageHeader,
} from '../../../../../components/Monetize';
import { DataTableActions } from '../../../../../components/Monetize/DataTable/MDataTableActions';
import { useTableColumns } from '../../../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../../../components/Monetize/DataTableNew/DataTableTypes';
import { ACTION_COLUMN_KEY } from '../../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { ROUTES } from '../../../../../constants';
import { getTeamsDetailsRoute } from '../../../../../constants/routes';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
} from '../../../../../utils';
import { navigateOrOpenNewWindow } from '../../../../../utils/misc';
import { useHandleDeleteTeam } from '../useHandleDeleteTeam';
import { TEAM_LIST_CATALOG, TeamListColumnContext } from './teamList.columns';

const DEFAULT_TEAM_PAGER: TDataTablePager = {
  ...DEFAULT_PAGER,
  sortField: 'name',
  sortOrder: 1,
};
const SEARCH_KEY = 'name';

export const TeamList = () => {
  const navigate = useNavigate();
  const tableRef = useRef<DataTableRef>(null);
  const [pager, setPager] = useState<TDataTablePager>(DEFAULT_TEAM_PAGER);
  const [filters, setFilters] = useState<FilterType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const filterParams = useMemo(
    () => buildFilterParamsRequestObject(filters, searchTerm, SEARCH_KEY),
    [filters, searchTerm],
  );

  // A new search must start from the first page, otherwise a stale page
  // index can hide matches that now fit on page one.
  const handleSearchTermChange = useCallback((nextSearchTerm: string) => {
    setSearchTerm(nextSearchTerm);
    setPager((currentPager) => ({ ...currentPager, page: 0, first: 0 }));
  }, []);

  const { isLoading, data: teams } = useGetTeams({
    config: pager,
    filters: filterParams,
  });
  const { handleDeleteTeam, deleteLoading } = useHandleDeleteTeam();

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading: isLoading,
    totalElements: teams?.totalElements ?? 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const actionBodyTemplate = useCallback(
    ({ id, name }: ITeamResponseSchema) => (
      <MFlex alignItems="center">
        <DataTableActions
          actions={[
            {
              title: 'Delete',
              color: 'danger',
              enabled: true,
              isDisabled: deleteLoading,
              action: () => handleDeleteTeam({ teamName: name, teamId: id }),
            },
          ]}
        />
      </MFlex>
    ),
    [deleteLoading, handleDeleteTeam],
  );

  const columnContext = useMemo<TeamListColumnContext>(
    () => ({ actionBodyTemplate }),
    [actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    TEAM_LIST_CATALOG,
    columnContext,
  );

  return (
    <MPageContainer alignItems="stretch" data-testid="teams-home-pg">
      <MSettingsPageHeader title="Teams">
        {!isTrulyEmptyList && (
          <MHStack gap="4">
            <MPageSearchInput
              placeholderKey="Name"
              value={searchTerm}
              onChange={handleSearchTermChange}
            />
            <MButton variant="primary" data-testid="new-team-btn" asChild>
              <ReactDomLink to="new">New</ReactDomLink>
            </MButton>
          </MHStack>
        )}
      </MSettingsPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={teams?.content ?? []}
        isLoading={isLoading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={teams?.totalElements ?? 0}
        totalPages={teams?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={filters}
        onFiltersChange={setFilters}
        resetFilter={() => {
          setFilters([]);
          setSearchTerm('');
          tableRef.current?.resetFilters();
        }}
        onRowClick={(row, _tableRow, openInNewWindow) =>
          navigateOrOpenNewWindow(
            navigate,
            getTeamsDetailsRoute(row.id),
            openInNewWindow,
          )
        }
        emptyProps={{
          mainMessage: 'Looks like there are no teams here.',
          btnLabel: 'New Team',
          to: ROUTES.SETTINGS_TEAMS_NEW,
        }}
      />
    </MPageContainer>
  );
};
